import { HeartIcon, Volume2Icon } from "lucide-react";
import { animate, motion, useMotionValue, useTransform } from "motion/react";
import { useRef } from "react";

import { Button } from "@/components/ui/button";
import { Direction, Language } from "@/lib/constant";
import { nativeBridge } from "@/lib/native";
import { cn } from "@/lib/utils";
import type { FlashcardWord } from "@/types/common";

const SWIPE_THRESHOLD = 120;
const VELOCITY_THRESHOLD = 500;

type FlashCardProps = {
  word: FlashcardWord;
  isFlipped: boolean;
  isInteractive: boolean;
  isTextVisible: boolean;
  frontSide: Language;
  trackCategories: boolean;
  isFavorited: boolean;
  onFlip: () => void;
  onSwipe: (direction: Direction) => void;
  onSwipeStart: (direction: Direction) => void;
  onDragUpdate: (direction: Direction | null) => void;
  onSpeak: () => void;
  onToggleFavorite: () => void;
  stackStyle?: React.CSSProperties;
};

export function FlashCard({
  word,
  isFlipped,
  isInteractive,
  isTextVisible,
  frontSide,
  trackCategories,
  isFavorited,
  onFlip,
  onSwipe,
  onSwipeStart,
  onDragUpdate,
  onSpeak,
  onToggleFavorite,
  stackStyle,
}: FlashCardProps) {
  const frontText = frontSide === Language.ENGLISH ? word.word : word.meaning;
  const backText = frontSide === Language.ENGLISH ? word.meaning : word.word;
  const frontIsWord = frontSide === Language.ENGLISH;
  const didDragRef = useRef(false);

  const x = useMotionValue(0);
  const rotate = useTransform(x, [-300, 300], [-15, 15]);
  const leftBorderOpacity = useTransform(x, [-SWIPE_THRESHOLD, 0], [1, 0]);
  const rightBorderOpacity = useTransform(x, [0, SWIPE_THRESHOLD], [0, 1]);

  const learningOpacity = useTransform(x, [-40, -SWIPE_THRESHOLD], [0, 1]);
  const knowOpacity = useTransform(x, [40, SWIPE_THRESHOLD], [0, 1]);

  function handleDragStart() {
    didDragRef.current = true;
  }

  function handleDrag(_: unknown, info: { offset: { x: number } }) {
    const { x: offsetX } = info.offset;
    if (offsetX < -20) {
      onDragUpdate(Direction.LEFT);
    } else if (offsetX > 20) {
      onDragUpdate(Direction.RIGHT);
    } else {
      onDragUpdate(null);
    }
  }

  function handleDragEnd(_: unknown, info: { offset: { x: number }; velocity: { x: number } }) {
    const { offset, velocity } = info;
    const isSwipeLeft = offset.x < -SWIPE_THRESHOLD || velocity.x < -VELOCITY_THRESHOLD;
    const isSwipeRight = offset.x > SWIPE_THRESHOLD || velocity.x > VELOCITY_THRESHOLD;

    if (isSwipeLeft || isSwipeRight) {
      const dir = isSwipeLeft ? Direction.LEFT : Direction.RIGHT;
      const target = dir === Direction.LEFT ? -window.innerWidth * 1.5 : window.innerWidth * 1.5;
      nativeBridge.system.haptic("medium");
      onSwipeStart(dir);
      animate(x, target, { duration: 0.3, ease: "easeOut" });
      setTimeout(() => onSwipe(dir), 300);
    } else {
      animate(x, 0, { type: "spring", stiffness: 300, damping: 30 });
      onDragUpdate(null);
    }

    setTimeout(() => {
      didDragRef.current = false;
    }, 50);
  }

  return (
    <motion.div
      className="absolute inset-0"
      style={{ x, rotate, ...stackStyle }}
      drag={isInteractive ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.9}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
    >
      {/* 보더 오버레이 (카테고리 분류 활성화 시에만 표시) */}
      {trackCategories && (
        <>
          <motion.div
            className="absolute inset-0 rounded-2xl pointer-events-none z-10 border-3 border-orange-300"
            style={{ opacity: leftBorderOpacity }}
          />
          <motion.div
            className="absolute inset-0 rounded-2xl pointer-events-none z-10 border-3 border-emerald-300"
            style={{ opacity: rightBorderOpacity }}
          />
        </>
      )}

      {/* 카드 */}
      <div
        className="relative w-full h-full cursor-pointer"
        style={{ transformStyle: "preserve-3d", perspective: "1000px" }}
        onClick={
          isInteractive
            ? () => {
                if (!didDragRef.current) {
                  onFlip();
                }
              }
            : undefined
        }
      >
        {/* 앞면 */}
        <div
          className="absolute inset-0 bg-card flex flex-col items-center justify-center rounded-2xl"
          style={{
            backfaceVisibility: "hidden",
            transition: "transform 0.5s",
            transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          <div
            className="absolute top-4 left-4 right-4 flex justify-between"
            style={{ visibility: isTextVisible ? "visible" : "hidden" }}
          >
            {/* 음성 재생 버튼 */}
            {frontIsWord && (
              <Button
                variant="ghost"
                size="icon"
                className="p-2 transition-colors"
                onClick={e => {
                  e.stopPropagation();
                  onSpeak();
                }}
              >
                <Volume2Icon className="size-5 text-muted-foreground" />
              </Button>
            )}

            {/* 즐겨찾기 버튼 */}
            <Button
              variant="ghost"
              size="icon"
              className={cn("p-2 transition-colors", !frontIsWord && "ml-auto")}
              onClick={e => {
                e.stopPropagation();
                onToggleFavorite();
              }}
            >
              <HeartIcon
                className={cn(
                  "size-5 transition-colors",
                  isFavorited ? "text-secondary fill-secondary" : "text-muted-foreground",
                )}
              />
            </Button>
          </div>

          <span
            className={
              frontIsWord
                ? "text-4xl font-black text-tertiary"
                : "text-2xl font-bold text-muted-foreground px-6 text-center"
            }
            style={{ visibility: isTextVisible ? "visible" : "hidden" }}
          >
            {frontText}
          </span>
        </div>

        {/* 뒷면 */}
        <div
          className="absolute inset-0 rounded-2xl bg-card shadow-sm flex flex-col items-center justify-center"
          style={{
            backfaceVisibility: "hidden",
            transition: "transform 0.5s",
            transform: isFlipped ? "rotateY(0deg)" : "rotateY(-180deg)",
          }}
        >
          <span
            className={
              backText === word.word
                ? "text-4xl font-black text-tertiary"
                : "text-2xl font-bold text-muted-foreground px-6 text-center"
            }
            style={{ visibility: isTextVisible ? "visible" : "hidden" }}
          >
            {backText}
          </span>
        </div>

        {/* 학습 중 / 알고 있음 레이블 (카테고리 분류 활성화 시에만 표시) */}
        {trackCategories && (
          <>
            <motion.div
              className="absolute inset-0 bg-card flex items-center justify-center z-20 pointer-events-none rounded-2xl"
              style={{ opacity: learningOpacity }}
            >
              <span className="text-3xl font-black bg-card text-orange-300 rounded-xl">학습 중</span>
            </motion.div>

            <motion.div
              className="absolute inset-0 bg-card flex items-center justify-center z-20 pointer-events-none rounded-2xl"
              style={{ opacity: knowOpacity }}
            >
              <span className="text-3xl font-black bg-card text-emerald-300 rounded-xl">알고 있음</span>
            </motion.div>
          </>
        )}
      </div>
    </motion.div>
  );
}
